// src/hooks/useSlaAi.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — data hooks for the SLA AI screens
//
//   useSlaAiResource(loader, deps, { live })  generic: loading / error / data / refresh,
//                                             aborts stale requests, isMounted-safe,
//                                             refreshes live on SLA / work order events
//   useSlaAiOverview()      overview KPIs + hot jobs
//   useSlaAiDecisions(f)    decision log with filters + "load more"
//   useSlaScoreboard(f)     tech / contractor reliability ranking
//   useSlaAiInbox()         SLA AI notifications + markRead
//   useSlaAiActions()       approve / reject / revert / escalate / runNow / preview
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import slaAiApi from '../services/slaAiApi';
import useFlowSenseLive from './useFlowSenseLive';

const LIVE_GROUPS = ['sla', 'workOrders'];

export function useSlaAiResource(loader, deps = [], { live = true, debounceMs = 1200, initial = null } = {}) {
    const [state, setState] = useState({ data: initial, loading: true, refreshing: false, error: null, loadedAt: null });
    const mounted = useRef(true);
    const ctrl = useRef(null);
    const loaderRef = useRef(loader);
    loaderRef.current = loader;

    const load = useCallback(async ({ quiet = false } = {}) => {
        ctrl.current?.abort?.();
        const ac = typeof AbortController === 'function' ? new AbortController() : null;
        ctrl.current = ac;
        setState(s => ({ ...s, loading: !quiet && s.data == null, refreshing: quiet || s.data != null, error: quiet ? s.error : null }));
        try {
            const data = await loaderRef.current(ac?.signal);
            if (!mounted.current || ctrl.current !== ac) return;
            setState({ data, loading: false, refreshing: false, error: null, loadedAt: new Date().toISOString() });
        } catch (e) {
            if (e?.name === 'AbortError' || !mounted.current || ctrl.current !== ac) return;
            setState(s => ({ ...s, loading: false, refreshing: false, error: e?.message || 'Something went wrong.' }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    useEffect(() => {
        mounted.current = true;
        load();
        return () => { mounted.current = false; ctrl.current?.abort?.(); };
    }, [load]);

    useFlowSenseLive(LIVE_GROUPS, () => load({ quiet: true }), { debounceMs, enabled: live });

    const refresh = useCallback(() => load({ quiet: true }), [load]);
    return useMemo(() => ({ ...state, refresh, reload: load }), [state, refresh, load]);
}

export const useSlaAiOverview = (opts) => useSlaAiResource((signal) => slaAiApi.overview(signal), [], opts);

export function useSlaAiDecisions(filters = {}, { pageSize = 25, live = true } = {}) {
    const key = JSON.stringify(filters);
    const [limit, setLimit] = useState(pageSize);
    useEffect(() => { setLimit(pageSize); }, [key, pageSize]);
    const res = useSlaAiResource(
        (signal) => slaAiApi.decisions({ ...filters, limit }, signal).then(r => ({ rows: r.data || [], total: r.total || 0 })),
        [key, limit], { live, initial: null });
    const loadMore = useCallback(() => setLimit(l => Math.min(200, l + pageSize)), [pageSize]);
    const rows = res.data?.rows || [];
    return { ...res, rows, total: res.data?.total || 0, hasMore: rows.length < (res.data?.total || 0) && limit < 200, loadMore };
}

export const useSlaScoreboard = (filters = {}, opts) => {
    const key = JSON.stringify(filters);
    return useSlaAiResource((signal) => slaAiApi.scoreboard(filters, signal).then(r => r.data || []), [key], opts);
};

const INBOX_EVENT = 'flowsense:sla-ai-inbox';

export function useSlaAiInbox({ limit = 30, live = true } = {}) {
    const res = useSlaAiResource((signal) => slaAiApi.notifications({ limit }, signal).then(r => ({ rows: r.data || [], unread: r.unread || 0 })), [limit], { live, debounceMs: 800 });
    const { refresh } = res;
    // Several inbox views (header bell + Inbox tab) stay in sync after a mark-read
    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const h = () => refresh();
        window.addEventListener(INBOX_EVENT, h);
        return () => window.removeEventListener(INBOX_EVENT, h);
    }, [refresh]);
    const markRead = useCallback(async (id = 'all') => {
        await slaAiApi.markRead(id);
        try { window.dispatchEvent(new window.CustomEvent(INBOX_EVENT)); } catch { refresh(); }
    }, [refresh]);
    return { ...res, rows: res.data?.rows || [], unread: res.data?.unread || 0, markRead };
}

/** Mutations with a per-action busy flag. Each resolves to the server response or throws. */
export function useSlaAiActions() {
    const [busy, setBusy] = useState(null);
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);
    const run = useCallback(async (name, fn) => {
        setBusy(name);
        try { return await fn(); } finally { if (mounted.current) setBusy(null); }
    }, []);
    return useMemo(() => ({
        busy,
        approve: (id, opts) => run(`approve:${id}`, () => slaAiApi.approve(id, opts)),
        reject: (id, opts) => run(`reject:${id}`, () => slaAiApi.reject(id, opts)),
        revert: (id, opts) => run(`revert:${id}`, () => slaAiApi.revert(id, opts)),
        escalate: (woId, opts) => run(`escalate:${woId}`, () => slaAiApi.escalate(woId, opts)),
        runNow: () => run('run', () => slaAiApi.runNow()),
    }), [busy, run]);
}
